/**
 * The collage (1.168.0): how two to four photos of a post stand together.
 *
 * Decided from use as an arrangement of the post's own photos, not a
 * composed picture: the server keeps the style (`Collage.php`), the photos
 * stay photos. **Which layouts fit which number comes from the server**
 * (`collage_layouts` with the settings), so the sheet offers what the server
 * takes; this file only names them for the eye and turns a style into the
 * variables of the stylesheet.
 */

import { NEUTRAL_ADJUST, photoFilter, type FilterStep, type PhotoAdjust, type PhotoGrade } from './photoEdit'

export interface CollageStyle {
  layout: string
  radius: number
  gap: number
  color: string
  /** The crop of each photo (1.176.0), by media id; a photo without one stands in the middle and fills. */
  crops?: CollageCrop[]
  /** The edit of the whole collage (1.176.0): a key of the server's filters, its strength, the sliders in percent. */
  filter?: string
  strength?: number
  brightness?: number
  contrast?: number
  saturation?: number
  /** The colour grading (1.177.0); null or missing changes nothing. */
  grade?: PhotoGrade | null
}

/** A photo's crop in a collage (1.176.0): the model of `lib/ausschnitt.ts`, at one photo. */
export interface CollageCrop {
  media_id: number
  x: number
  y: number
  zoom: number
}

/** The edit of a style, neutral where it has none (a collage from before 1.176.0). */
export function adjustOf(style: CollageStyle): PhotoAdjust {
  return {
    filter: style.filter ?? NEUTRAL_ADJUST.filter,
    strength: style.strength ?? NEUTRAL_ADJUST.strength,
    brightness: style.brightness ?? NEUTRAL_ADJUST.brightness,
    contrast: style.contrast ?? NEUTRAL_ADJUST.contrast,
    saturation: style.saturation ?? NEUTRAL_ADJUST.saturation,
    // As set, also without effect: the sheet keeps a hue chosen before its
    // saturation. What draws and what compares asks `activeGrade()`.
    grade: style.grade ?? null,
  }
}

/** The crop of one photo, `undefined` for the middle. */
export function cropOf(style: CollageStyle, mediaId: number | undefined): CollageCrop | undefined {
  return mediaId === undefined ? undefined : style.crops?.find((c) => c.media_id === mediaId)
}

export const COLLAGE_FEWEST = 2
export const COLLAGE_MOST = 4

/**
 * The layout that fits a number of photos: the one asked for when it fits,
 * else the first of the list (the server's default). After a photo was
 * added or removed in the sheet the old layout may not fit any more; the
 * sheet then shows this one instead of offering a send the server refuses.
 */
export function fittingLayout(layouts: Record<string, string[]>, count: number, wanted: string): string {
  const list = layouts[String(count)] ?? []
  return list.includes(wanted) ? wanted : (list[0] ?? wanted)
}

/**
 * The style as variables of `.collage`: three numbers from the server or the
 * sheet, the one thing a class cannot know. The colour is `#rrggbb`, checked
 * by the server before it is stored.
 */
export function collageVariables(style: CollageStyle, filters?: Record<string, FilterStep[]>): Record<string, string> {
  return {
    // The edit (1.176.0) at the photos, not at the box: on the box it would colour the frame too.
    '--collage-filter': photoFilter(adjustOf(style), filters),
    '--collage-radius': `${style.radius}px`,
    // The frame's corner (1.169.0): as `Collage::outerRadius()` on the server.
    '--collage-outer-radius': `${style.radius > 0 ? style.radius + style.gap : 0}px`,
    '--collage-gap': `${style.gap}px`,
    '--collage-color': style.color,
  }
}

/**
 * Where a new collage of this trip starts (1.172.0): the trip's own values
 * (`post_defaults`, copied from the administration when the trip was made),
 * the administration's while the trip is still loading, the built-in numbers
 * while both are. Read while drawing, never copied at opening: either may
 * still be on its way.
 */
export function newCollageStyle(
  trip: { collage_radius: number; collage_gap: number; collage_color: string } | undefined,
  administration: { collage_radius: number; collage_gap: number; collage_color: string } | undefined,
): Pick<CollageStyle, 'radius' | 'gap' | 'color'> {
  const from = trip ?? administration
  return {
    radius: from?.collage_radius ?? 12,
    gap: from?.collage_gap ?? 4,
    color: from?.collage_color ?? '#ffffff',
  }
}
